import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { formatEuro, formatEuroExact, formatPercent, parseAmountInput } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Card, Pill, Select, Skeleton, useToast } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { SettingsPageHeader } from '../../settings/SettingsFrame';
import {
  dealKeys,
  patchPropertyRate,
  putRoleDefaults,
  putUserCommissionSettings,
  useCommissionSettings,
  useUserCommissionSettings,
} from '../api';
import type { CommissionRole, CommissionSettings } from '../types';
import { useUserDirectory } from '../../users/directory';
import styles from './settings.module.css';
import frameStyles from '../../settings/SettingsFrame.module.css';

/**
 * Admin 07 „Provision für Immobilien": the property rates and caps (inline, `PATCH …/properties/:id`),
 * the role distribution (`PUT …/role-defaults`, saved with „Speichern"), the preview at the example unit
 * and the per-user overrides (`GET/PUT …/users/:userId`).
 */

/** Roles that take part in a property commission (closer, team lead, setter, referrer) — the per-user candidates. */
const COMMISSION_ROLE_KEYS = ['sales_lead', 'advisor', 'setter', 'referrer'] as const;

const PERCENT_RANGE = 'Der Anteil muss zwischen 0 und 100 % liegen.';

function percentText(value: number | null): string {
  return value === null ? '' : String(value).replace('.', ',');
}

function parsePercent(text: string): number | null | 'invalid' {
  const trimmed = text.trim().replace('%', '').trim();
  if (trimmed === '') return null;
  const value = Number(trimmed.replace(',', '.'));
  return Number.isFinite(value) ? value : 'invalid';
}

function PropertyRow({
  row,
  onSaved,
  onError,
}: {
  row: CommissionSettings['properties'][number];
  onSaved: (settings: CommissionSettings) => void;
  onError: (message: string) => void;
}) {
  const [rate, setRate] = useState(percentText(row.ratePct));
  const [cap, setCap] = useState(
    row.capPerDealCents === null ? '' : formatEuro(row.capPerDealCents).replace(' €', ''),
  );
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setRate(percentText(row.ratePct));
    setCap(row.capPerDealCents === null ? '' : formatEuro(row.capPerDealCents).replace(' €', ''));
  }, [row.ratePct, row.capPerDealCents]);

  async function save(field: 'ratePct' | 'capPerDealCents') {
    let body: { ratePct?: number | null; capPerDealCents?: number | null };
    if (field === 'ratePct') {
      const parsed = parsePercent(rate);
      if (parsed === 'invalid' || (parsed !== null && (parsed < 0 || parsed > 100))) {
        setError(PERCENT_RANGE);
        return;
      }
      if (parsed === row.ratePct) return;
      body = { ratePct: parsed };
    } else {
      const cents = cap.trim() === '' ? null : parseAmountInput(cap);
      if (cap.trim() !== '' && cents === null) {
        setError('Bitte einen Betrag in Euro angeben.');
        return;
      }
      if (cents === row.capPerDealCents) return;
      body = { capPerDealCents: cents };
    }
    setError(null);
    setPending(true);
    try {
      onSaved(await patchPropertyRate(row.id, body));
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(err.fields['ratePct'] ?? err.fields['capPerDealCents'] ?? err.message);
      } else onError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className={styles.row}>
      <span className={styles.rowName}>{row.name}</span>
      <span>
        <input
          aria-label={`Satz vom Kaufpreis ${row.name}`}
          className={clsx(
            styles.inlineInput,
            error && styles.inlineInputInvalid,
            row.missing && rate === '' && styles.inlineInputMissing,
          )}
          value={rate}
          placeholder={row.missing ? 'fehlt' : ''}
          inputMode="decimal"
          disabled={pending}
          onChange={(event) => setRate(event.target.value)}
          onBlur={() => void save('ratePct')}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
        />
      </span>
      <span>
        <input
          aria-label={`Kappung je Deal ${row.name}`}
          className={clsx(styles.inlineInput, error && styles.inlineInputInvalid)}
          value={cap}
          placeholder="—"
          inputMode="decimal"
          disabled={pending}
          onChange={(event) => setCap(event.target.value)}
          onBlur={() => void save('capPerDealCents')}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
        />
      </span>
      <span className={clsx(styles.rowState, row.missing && styles.rowMissing)}>
        {row.stateLabel}
        {error ? <span className={styles.fieldError}>{error}</span> : null}
      </span>
    </div>
  );
}

interface RoleDraft {
  split: string;
  cap: string;
}

export function CommissionSettingsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const settings = useCommissionSettings();
  const participants = useUserDirectory({ roles: COMMISSION_ROLE_KEYS });
  const [draft, setDraft] = useState<Record<string, RoleDraft>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [userId, setUserId] = useState('');
  const userSettings = useUserCommissionSettings(userId || null);
  const [userDraft, setUserDraft] = useState<Record<string, RoleDraft>>({});
  const [userErrors, setUserErrors] = useState<Record<string, string>>({});
  const [userSaving, setUserSaving] = useState(false);

  const data = settings.data;

  useEffect(() => {
    if (!data) return;
    setDraft(
      Object.fromEntries(
        data.roleDefaults.map((role) => [
          role.role,
          { split: percentText(role.splitPct), cap: percentText(role.capPct) },
        ]),
      ),
    );
  }, [data]);

  useEffect(() => {
    if (!userSettings.data) return;
    setUserDraft(
      Object.fromEntries(
        userSettings.data.roles.map((role) => [
          role.role,
          {
            split: role.source === 'user' ? percentText(role.splitPct) : '',
            cap: role.source === 'user' ? percentText(role.capPct) : '',
          },
        ]),
      ),
    );
    setUserErrors({});
  }, [userSettings.data]);

  const organizationPct = useMemo(() => {
    if (!data) return null;
    let flat = 0;
    let line = 0;
    for (const role of data.roleDefaults) {
      const parsed = parsePercent(draft[role.role]?.split ?? '');
      const value = parsed === 'invalid' || parsed === null ? role.splitPct : parsed;
      if (role.role === 'closer' || role.role === 'team_lead') line = Math.max(line, value);
      else flat += value;
    }
    return Math.max(0, 100 - flat - line);
  }, [data, draft]);

  function applySettings(next: CommissionSettings) {
    queryClient.setQueryData(dealKeys.settings(), next);
    void queryClient.invalidateQueries({ queryKey: dealKeys.settings() });
  }

  async function saveDefaults() {
    if (!data || saving) return;
    const body: { role: CommissionRole; splitPct: number; capPct: number | null }[] = [];
    const next: Record<string, string> = {};
    for (const role of data.roleDefaults) {
      const entry = draft[role.role] ?? { split: '', cap: '' };
      const split = parsePercent(entry.split);
      const cap = parsePercent(entry.cap);
      if (split === 'invalid' || split === null || split < 0 || split > 100)
        next[`${role.role}.split`] = PERCENT_RANGE;
      if (cap === 'invalid' || (cap !== null && (cap < 0 || cap > 100)))
        next[`${role.role}.cap`] = 'Der Deckel muss zwischen 0 und 100 % liegen.';
      if (split !== 'invalid' && split !== null && cap !== 'invalid')
        body.push({ role: role.role, splitPct: split, capPct: cap });
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSaving(true);
    setBanner(null);
    try {
      applySettings(await putRoleDefaults(body));
      setSavedAt(
        new Date().toLocaleTimeString('de-DE', {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Europe/Berlin',
        }),
      );
      toast({ message: 'Verteilung gespeichert.' });
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        const fields = error.fields;
        const mapped: Record<string, string> = {};
        if (fields['splitPct']) mapped['splitPct'] = fields['splitPct'];
        if (fields['capPct']) mapped['capPct'] = fields['capPct'];
        setErrors(mapped);
        if (Object.keys(mapped).length === 0) setBanner(error.message);
      } else setBanner(errorMessage(error));
    } finally {
      setSaving(false);
    }
  }

  async function saveUser() {
    if (!userSettings.data || userSaving) return;
    const roles: { role: CommissionRole; splitPct: number | null; capPct: number | null }[] = [];
    const next: Record<string, string> = {};
    for (const role of userSettings.data.roles) {
      const entry = userDraft[role.role] ?? { split: '', cap: '' };
      const split = parsePercent(entry.split);
      const cap = parsePercent(entry.cap);
      if (split === 'invalid' || (split !== null && (split < 0 || split > 100)))
        next[role.role] = PERCENT_RANGE;
      if (cap === 'invalid' || (cap !== null && (cap < 0 || cap > 100)))
        next[role.role] = 'Der Deckel muss zwischen 0 und 100 % liegen.';
      if (split !== 'invalid' && cap !== 'invalid')
        roles.push({ role: role.role, splitPct: split, capPct: cap });
    }
    setUserErrors(next);
    if (Object.keys(next).length > 0) return;
    setUserSaving(true);
    try {
      queryClient.setQueryData(dealKeys.userSettings(userId), await putUserCommissionSettings(userId, roles));
      toast({ message: 'Abweichung gespeichert.' });
    } catch (error) {
      if (error instanceof ApiError && error.status === 400)
        setUserErrors({ form: error.fields['splitPct'] ?? error.fields['capPct'] ?? error.message });
      else setUserErrors({ form: errorMessage(error) });
    } finally {
      setUserSaving(false);
    }
  }

  const userOptions = useMemo(
    () => (participants.data ?? []).map((user) => ({ value: user.id, label: user.name })),
    [participants.data],
  );

  return (
    <div className={frameStyles.page}>
      <SettingsPageHeader
        eyebrow="Organisation"
        title="Provision für Immobilien"
        description={
          data?.intro ??
          'Der Satz je Objekt kommt aus dem Vertrag mit dem Bauträger. Änderungen gelten nur für neue Reservierungen, nicht rückwirkend.'
        }
        actions={
          <>
            {savedAt ? <span className={styles.savedNote}>Gespeichert · {savedAt}</span> : null}
            <Button loading={saving} disabled={!data} onClick={() => void saveDefaults()}>
              Speichern
            </Button>
          </>
        }
      />
      {settings.isError ? <Banner tone="danger">{errorMessage(settings.error)}</Banner> : null}
      {banner ? <Banner tone="danger">{banner}</Banner> : null}
      {settings.isPending ? (
        <div className={styles.grid}>
          <div className={styles.column}>
            <Card className={styles.card}>
              <Skeleton width="40%" height={14} />
              <Skeleton variant="block" height={44} style={{ marginTop: 12 }} />
              <Skeleton variant="block" height={44} style={{ marginTop: 8 }} />
            </Card>
          </div>
          <div className={styles.column}>
            <Card className={styles.card}>
              <Skeleton width="50%" height={10} />
              <Skeleton width="70%" height={26} style={{ marginTop: 12 }} />
            </Card>
          </div>
        </div>
      ) : null}
      {data ? (
        <div className={styles.grid}>
          <div className={styles.column}>
            <Card className={styles.tableCard} aria-label="Objekte">
              <div className={clsx(styles.row, styles.rowHead)}>
                <span>Objekt</span>
                <span>Satz vom Kaufpreis</span>
                <span>Kappung je Deal</span>
                <span>Stand</span>
              </div>
              {data.properties.length === 0 ? (
                <div className={styles.empty}>Noch kein Objekt angelegt.</div>
              ) : null}
              {data.properties.map((row) => (
                <PropertyRow key={row.id} row={row} onSaved={applySettings} onError={setBanner} />
              ))}
            </Card>
            <Card className={styles.card} aria-label="Verteilung je Rolle">
              <div className={styles.cardTitle}>Verteilung je Rolle</div>
              <p className={styles.cardText}>
                Anteil an der Provision eines Deals. Summe höchstens 100 %, der Rest bleibt bei der
                Organisation.
              </p>
              <div className={styles.kv}>
                {data.roleDefaults.map((role) => (
                  <div key={role.role} className={styles.kvRow}>
                    <span className={styles.kvKey}>{role.roleLabel}</span>
                    <span className={styles.kvInputs}>
                      <input
                        aria-label={`Anteil ${role.roleLabel}`}
                        className={clsx(
                          styles.inlineInput,
                          styles.kvInput,
                          errors[`${role.role}.split`] && styles.inlineInputInvalid,
                        )}
                        value={draft[role.role]?.split ?? ''}
                        inputMode="decimal"
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            [role.role]: { split: event.target.value, cap: draft[role.role]?.cap ?? '' },
                          })
                        }
                      />
                      <span className={styles.kvUnit}>%</span>
                      <span className={styles.kvUnit}>Deckel</span>
                      <input
                        aria-label={`Deckel ${role.roleLabel}`}
                        className={clsx(
                          styles.inlineInput,
                          styles.kvInput,
                          errors[`${role.role}.cap`] && styles.inlineInputInvalid,
                        )}
                        value={draft[role.role]?.cap ?? ''}
                        placeholder="—"
                        inputMode="decimal"
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            [role.role]: { split: draft[role.role]?.split ?? '', cap: event.target.value },
                          })
                        }
                      />
                      <span className={styles.kvUnit}>% der Basis</span>
                    </span>
                    {errors[`${role.role}.split`] || errors[`${role.role}.cap`] ? (
                      <span className={styles.fieldError} role="alert">
                        {errors[`${role.role}.split`] ?? errors[`${role.role}.cap`]}
                      </span>
                    ) : null}
                  </div>
                ))}
                <div className={styles.kvRow}>
                  <span className={styles.kvKey}>Organisation</span>
                  <span className={styles.kvValue}>
                    {formatPercent(organizationPct ?? data.organizationPct, 0)} (Rest)
                  </span>
                </div>
              </div>
              {errors['splitPct'] || errors['capPct'] ? (
                <div className={styles.fieldError} role="alert">
                  {errors['splitPct'] ?? errors['capPct']}
                </div>
              ) : null}
            </Card>
            <Card className={styles.card} aria-label="Abweichung je Nutzer">
              <div className={styles.cardTitle}>Abweichung je Nutzer</div>
              <p className={styles.cardText}>
                Eine Abweichung überschreibt die Verteilung für diese Person. Leere Felder übernehmen den
                Standard.
              </p>
              <div style={{ marginTop: 12, maxWidth: 320 }}>
                <Select
                  label="Nutzer"
                  size="compact"
                  value={userId}
                  placeholder="Nutzer wählen"
                  options={userOptions}
                  onChange={(event) => setUserId(event.target.value)}
                />
              </div>
              {userSettings.isPending && userId ? (
                <Skeleton variant="block" height={44} style={{ marginTop: 12 }} />
              ) : null}
              {userSettings.isError ? (
                <Banner tone="danger">{errorMessage(userSettings.error)}</Banner>
              ) : null}
              {userSettings.data ? (
                <>
                  <div className={styles.kv}>
                    {userSettings.data.roles.map((role) => (
                      <div key={role.role} className={styles.kvRow}>
                        <span className={styles.kvKey}>
                          {role.roleLabel}
                          {role.source === 'user' ? (
                            <Pill variant="accent" style={{ marginLeft: 8 }}>
                              abweichend
                            </Pill>
                          ) : null}
                        </span>
                        <span className={styles.kvInputs}>
                          <input
                            aria-label={`Anteil ${role.roleLabel} · ${userSettings.data.user.name}`}
                            className={clsx(
                              styles.inlineInput,
                              styles.kvInput,
                              userErrors[role.role] && styles.inlineInputInvalid,
                            )}
                            value={userDraft[role.role]?.split ?? ''}
                            placeholder={percentText(role.splitPct)}
                            inputMode="decimal"
                            onChange={(event) =>
                              setUserDraft({
                                ...userDraft,
                                [role.role]: {
                                  split: event.target.value,
                                  cap: userDraft[role.role]?.cap ?? '',
                                },
                              })
                            }
                          />
                          <span className={styles.kvUnit}>%</span>
                          <span className={styles.kvUnit}>Deckel</span>
                          <input
                            aria-label={`Deckel ${role.roleLabel} · ${userSettings.data.user.name}`}
                            className={clsx(styles.inlineInput, styles.kvInput)}
                            value={userDraft[role.role]?.cap ?? ''}
                            placeholder={role.capPct === null ? '—' : percentText(role.capPct)}
                            inputMode="decimal"
                            onChange={(event) =>
                              setUserDraft({
                                ...userDraft,
                                [role.role]: {
                                  split: userDraft[role.role]?.split ?? '',
                                  cap: event.target.value,
                                },
                              })
                            }
                          />
                          <span className={styles.kvUnit}>% der Basis</span>
                        </span>
                        {userErrors[role.role] ? (
                          <span className={styles.fieldError}>{userErrors[role.role]}</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                  {userErrors['form'] ? (
                    <div className={styles.fieldError} role="alert">
                      {userErrors['form']}
                    </div>
                  ) : null}
                  <div style={{ marginTop: 14 }}>
                    <Button
                      variant="secondary"
                      size="small"
                      loading={userSaving}
                      onClick={() => void saveUser()}
                    >
                      Abweichung speichern
                    </Button>
                  </div>
                </>
              ) : null}
            </Card>
          </div>
          <div className={styles.column}>
            <Card className={styles.card} aria-label="Vorschau am Beispiel">
              <div className={styles.eyebrow}>Vorschau am Beispiel</div>
              {data.preview ? (
                <>
                  <div className={styles.previewUnit}>{data.preview.unitLabel}</div>
                  <div className={styles.previewTotal}>{formatEuroExact(data.preview.totalCents)}</div>
                  <div className={styles.kv}>
                    {data.preview.rows.map((row) => (
                      <div key={`${row.role}-${row.name}`} className={styles.kvRow}>
                        <span className={styles.kvKey}>
                          {row.role === 'organization' || row.name === row.roleLabel
                            ? row.roleLabel
                            : `${row.name} · ${row.roleLabel}`}
                        </span>
                        <span className={styles.kvValue}>{formatEuroExact(row.amountCents)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className={styles.previewUnit}>
                  Sobald ein Objekt einen Satz und eine bepreiste Einheit hat, rechnet DAMOKLES hier ein
                  Beispiel durch.
                </div>
              )}
            </Card>
            <div className={styles.hintBox}>{data.financingHint}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
